import React, { useState, useEffect } from 'react';
import { X, ShieldCheck, Target, Activity } from 'lucide-react';
import { SafetyObjective, IndicatorType, ThresholdMethodology } from '../types';
import { performanceService } from '../performanceService';
import { administrationService, Department } from '../../../administration/administrationService';
import { useEscapeKey } from '../../../../shared/useGlobalModalEscape';
import { useDialog } from '../../../../shared/DialogContext';

interface CreateSPIModalProps {
  objectives: SafetyObjective[];
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateSPIModal: React.FC<CreateSPIModalProps> = ({
  objectives,
  onClose,
  onSuccess,
}) => {
  useEscapeKey(onClose);
  const { showError, showWarning } = useDialog();

  const [objectiveId, setObjectiveId] = useState<string>(
    objectives.length > 0 ? objectives[0].id : ''
  );
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [indicatorType, setIndicatorType] = useState<IndicatorType>('LAGGING');
  const [frequency, setFrequency] = useState('MONTHLY');
  const [unit, setUnit] = useState('RATE');
  const [department, setDepartment] = useState('');
  const [ownerDepartment, setOwnerDepartment] = useState('');
  const [dataProviderDepartment, setDataProviderDepartment] = useState('');
  const [dataProviderInstructions, setDataProviderInstructions] = useState('');
  const [stakeholderDepartments, setStakeholderDepartments] = useState<string[]>([]);
  const [thresholdMethodology, setThresholdMethodology] = useState<ThresholdMethodology>('THREE_SIGMA');
  const [targetAmberValue, setTargetAmberValue] = useState<number | ''>('');
  const [targetRedValue, setTargetRedValue] = useState<number | ''>('');
  const [deptList, setDeptList] = useState<Department[]>([]);

  // Formula
  const [formulaExpression, setFormulaExpression] = useState('(numerator / denominator) * 1000');
  const [numeratorDescription, setNumeratorDescription] = useState('Count of Occurrences / Events');
  const [denominatorDescription, setDenominatorDescription] = useState('Total Flight Hours / Sectors');
  const [multiplier, setMultiplier] = useState<number>(1000);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    administrationService.getDepartments().then((depts) => {
      const activeDepts = depts.filter((d) => d.status === 'ACTIVE');
      setDeptList(activeDepts);
      if (activeDepts.length > 0) {
        if (!ownerDepartment) setOwnerDepartment(activeDepts[0].name);
        if (!dataProviderDepartment) setDataProviderDepartment(activeDepts[0].name);
        if (!department) setDepartment(activeDepts[0].name);
      }
    }).catch(() => {});
  }, []);

  // When objective changes, sync department and auto-populate contextual description if empty
  useEffect(() => {
    if (objectiveId) {
      const selectedObj = objectives.find((o) => o.id === objectiveId);
      if (selectedObj) {
        if (selectedObj.department) {
          setDepartment(selectedObj.department);
          setOwnerDepartment(selectedObj.department);
          if (!dataProviderDepartment) {
            setDataProviderDepartment(selectedObj.department);
          }
        }
        if (!description) {
          setDescription(`Measures achievement for ${selectedObj.code}: ${selectedObj.title}`);
        }
      }
    }
  }, [objectiveId, objectives]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!objectiveId) {
      showWarning('Please select an associated safety objective.', 'Objective Required');
      return;
    }
    const finalOwner = ownerDepartment || department;
    const finalProvider = dataProviderDepartment || finalOwner;
    try {
      setLoading(true);
      await performanceService.createIndicator({
        objectiveId,
        name,
        description,
        indicatorType,
        frequency,
        unit,
        department: finalOwner,
        ownerDepartment: finalOwner,
        dataProviderDepartment: finalProvider,
        dataProviderInstructions,
        stakeholderDepartments,
        thresholdMethodology,
        targetAmberValue: targetAmberValue !== '' ? Number(targetAmberValue) : undefined,
        targetRedValue: targetRedValue !== '' ? Number(targetRedValue) : undefined,
        createdBy: 'SAFETY_MANAGER',
        formula: {
          formulaExpression,
          numeratorDescription,
          denominatorDescription,
          multiplier: Number(multiplier),
        },
      });
      onSuccess();
    } catch (err: any) {
      showError(err, 'Failed to Define Safety Performance Indicator');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3 style={{ fontSize: '19px', fontWeight: 700, color: '#0f172a', margin: '0 0 4px 0' }}>
              Define Safety Performance Indicator (SPI)
            </h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.4 }}>
              ICAO Doc 9859 quantitative safety metrics, formulae, and monitoring frequency.
            </p>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Close"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="form-field">
              <label>Associated Safety Objective *</label>
              <select
                required
                value={objectiveId}
                onChange={(e) => setObjectiveId(e.target.value)}
              >
                {objectives.map((obj) => (
                  <option key={obj.id} value={obj.id}>
                    {obj.code} - {obj.title} ({obj.category})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>SPI Name / Indicator Headline *</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Unstabilized Approach Rate per 1,000 Landings"
              />
            </div>

            <div className="form-field">
              <label>Indicator Description & Target Scope *</label>
              <textarea
                required
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Monitored flight safety parameters, operational baseline, and alert thresholds..."
              />
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Indicator Classification</label>
                <select
                  value={indicatorType}
                  onChange={(e) => setIndicatorType(e.target.value as IndicatorType)}
                >
                  <option value="LAGGING">LAGGING (Outcome - Accidents, Incidents & Rates)</option>
                  <option value="LEADING">LEADING (Precursor - Training, Compliance & Audits)</option>
                </select>
              </div>

              <div className="form-field">
                <label>Measurement Frequency</label>
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                >
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                  <option value="ANNUALLY">Annually</option>
                </select>
              </div>
            </div>

            {/* SPI Governance Roles: Owner Department vs Data Provider Department */}
            <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#334155', textTransform: 'uppercase' }}>
                  SPI Governance & Accountability Roles
                </span>
                <span style={{ fontSize: '11px', color: '#64748b' }}>
                  Owner sets goals & targets • Data Provider inputs monthly metrics
                </span>
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: '#0284c7', fontWeight: 700 }}>● SPI Owner Department *</span>
                  </label>
                  <select
                    required
                    value={ownerDepartment}
                    onChange={(e) => {
                      setOwnerDepartment(e.target.value);
                      if (!dataProviderDepartment) setDataProviderDepartment(e.target.value);
                    }}
                  >
                    {deptList.length === 0 ? (
                      <option value="">Select Owner Department</option>
                    ) : (
                      deptList.map((d) => (
                        <option key={d.id} value={d.name}>
                          {d.name} ({d.department_code})
                        </option>
                      ))
                    )}
                  </select>
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                    Department owning and managing this safety indicator.
                  </span>
                </div>

                <div className="form-field">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: '#d97706', fontWeight: 700 }}>● Data Provider Department *</span>
                  </label>
                  <select
                    required
                    value={dataProviderDepartment}
                    onChange={(e) => setDataProviderDepartment(e.target.value)}
                  >
                    {deptList.length === 0 ? (
                      <option value="">Select Data Provider Department</option>
                    ) : (
                      deptList.map((d) => (
                        <option key={d.id} value={d.name}>
                          {d.name} ({d.department_code})
                        </option>
                      ))
                    )}
                  </select>
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                    Department responsible for feeding and recording monthly numbers.
                  </span>
                </div>
              </div>

              <div className="form-field" style={{ margin: 0 }}>
                <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>
                  Data Provider Instructions / Source Data Description (Optional)
                </label>
                <input
                  type="text"
                  value={dataProviderInstructions}
                  onChange={(e) => setDataProviderInstructions(e.target.value)}
                  placeholder="e.g. Monthly OFDM exceedances summary from Flight Safety Server or TechLog defects"
                  style={{ fontSize: '12px' }}
                />
              </div>

              {/* Stakeholder Departments (Item 11) */}
              <div className="form-field" style={{ margin: 0, paddingTop: '4px', borderTop: '1px dashed #cbd5e1' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Stakeholder Departments (Read-Only Observers)</span>
                  <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 'normal' }}>
                    View & alert visibility only • No data entry, action or risk creation
                  </span>
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                  {deptList.map((d) => {
                    const isSelected = stakeholderDepartments.includes(d.name);
                    const isOwnerOrProvider = d.name === ownerDepartment || d.name === dataProviderDepartment;
                    if (isOwnerOrProvider) return null;
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => {
                          setStakeholderDepartments((prev) =>
                            isSelected ? prev.filter((name) => name !== d.name) : [...prev, d.name]
                          );
                        }}
                        style={{
                          padding: '4px 10px',
                          fontSize: '11px',
                          borderRadius: '16px',
                          border: isSelected ? '1px solid #7c3aed' : '1px solid #cbd5e1',
                          background: isSelected ? '#f5f3ff' : '#ffffff',
                          color: isSelected ? '#6d28d9' : '#475569',
                          fontWeight: isSelected ? 700 : 500,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <span>{isSelected ? '✓' : '+'}</span>
                        <span>{d.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Threshold Methodology (Item 13) */}
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#334155', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Activity size={14} color="#2563eb" /> Alert Level & Threshold Methodology
                </span>
                <span style={{ fontSize: '11px', color: '#64748b' }}>
                  Configurable per SPI
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div
                  onClick={() => setThresholdMethodology('THREE_SIGMA')}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: thresholdMethodology === 'THREE_SIGMA' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                    background: thresholdMethodology === 'THREE_SIGMA' ? '#eff6ff' : '#f8fafc',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ fontSize: '12px', fontWeight: 700, color: thresholdMethodology === 'THREE_SIGMA' ? '#1e40af' : '#334155' }}>
                    📐 EASA 3-Sigma Methodology
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                    Statistical baseline + 1σ, 2σ, 3σ standard deviations (Aviation SMS standard).
                  </div>
                </div>

                <div
                  onClick={() => setThresholdMethodology('SPECIFIC_TARGET')}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: thresholdMethodology === 'SPECIFIC_TARGET' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                    background: thresholdMethodology === 'SPECIFIC_TARGET' ? '#eff6ff' : '#f8fafc',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ fontSize: '12px', fontWeight: 700, color: thresholdMethodology === 'SPECIFIC_TARGET' ? '#1e40af' : '#334155' }}>
                    🎯 Specific Target Limits
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                    Fixed numeric thresholds: Amber warning limit and Red breach limit.
                  </div>
                </div>
              </div>

              {thresholdMethodology === 'SPECIFIC_TARGET' && (
                <div className="form-grid-2" style={{ marginTop: '6px', padding: '10px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div className="form-field" style={{ margin: 0 }}>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#d97706' }}>
                      Warning Amber Limit Value
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={targetAmberValue}
                      onChange={(e) => setTargetAmberValue(e.target.value === '' ? '' : Number(e.target.value))}
                      placeholder="e.g. 2.50"
                      style={{ fontSize: '12px' }}
                    />
                  </div>
                  <div className="form-field" style={{ margin: 0 }}>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#dc2626' }}>
                      Critical Red Limit Value
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={targetRedValue}
                      onChange={(e) => setTargetRedValue(e.target.value === '' ? '' : Number(e.target.value))}
                      placeholder="e.g. 4.00"
                      style={{ fontSize: '12px' }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="form-field">
              <label>Unit of Measurement</label>
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
              >
                <option value="RATE">Rate (Ratio)</option>
                <option value="PERCENTAGE">Percentage (%)</option>
                <option value="PER_1000_FLIGHTS">Per 1,000 Flights</option>
                <option value="PER_10000_HOURS">Per 10,000 Hours</option>
                <option value="COUNT">Absolute Count</option>
              </select>
            </div>

            {/* Formula Settings Card */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                Mathematical Calculation Formula (Normalization)
              </span>

              <div className="form-field">
                <label style={{ fontSize: '11px' }}>Numerator Description</label>
                <input
                  type="text"
                  value={numeratorDescription}
                  onChange={(e) => setNumeratorDescription(e.target.value)}
                  placeholder="e.g. Total Unstabilized Approaches Recorded"
                />
              </div>

              <div className="form-field">
                <label style={{ fontSize: '11px' }}>Denominator Description</label>
                <input
                  type="text"
                  value={denominatorDescription}
                  onChange={(e) => setDenominatorDescription(e.target.value)}
                  placeholder="e.g. Total Approaches Flown"
                />
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label style={{ fontSize: '11px' }}>Multiplier Factor</label>
                  <input
                    type="number"
                    value={multiplier}
                    onChange={(e) => setMultiplier(Number(e.target.value))}
                  />
                </div>

                <div className="form-field">
                  <label style={{ fontSize: '11px' }}>Formula Expression</label>
                  <input
                    type="text"
                    value={formulaExpression}
                    onChange={(e) => setFormulaExpression(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{ width: 'auto' }}
            >
              {loading ? 'Defining...' : 'Define Indicator'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSPIModal;
